// Office network for self check-in, per office.
//
// ⚠️ WHY: a self check-in recorded who and when, never where — a laptop at
// home and one at the office looked the same. KaziPeople now compares the
// address a check-in arrives from (the gateway's own reading, not something a
// browser can set) with the office's public internet address.
//
// Three settings: Off; Flag (accept, and mark "outside the office" on the
// Self check-ins review — the safe way to start); Only allow from the office.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Wifi } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getMyNetwork, upsertCapturePolicy,
  type CaptureMode, type CapturePolicyRow, type NetworkCheck,
} from '@/services/attendanceService';
import { looksPrivate, parseAddressList } from './officeNetwork';

export const OfficeNetworkPanel: React.FC<{
  branchId: string | null;
  mode: CaptureMode;
  row?: CapturePolicyRow;
  canWrite: boolean;
  onSaved: () => void;
}> = ({ branchId, mode, row, canWrite, onSaved }) => {
  const { t } = useTranslation('attendance');
  const [check, setCheck] = useState<NetworkCheck>(row?.network_check ?? 'off');
  const [text, setText] = useState((row?.office_networks ?? []).map((n) => n.replace(/\/32$/, '')).join('\n'));
  const [busy, setBusy] = useState<'save' | 'detect' | null>(null);

  const addresses = parseAddressList(text);
  const privateOnes = addresses.filter(looksPrivate);
  const blocker = privateOnes.length > 0
    ? t('network_private', '{{list}} is how the office looks from the inside. Use the public address: open this page on an office computer and click "Use this computer\'s address".', { list: privateOnes.join(', ') })
    : check === 'enforce' && addresses.length === 0
      ? t('network_need_address', 'Add the office\'s address before allowing check-in only from the office.')
      : '';

  const detect = async () => {
    setBusy('detect');
    try {
      const me = await getMyNetwork();
      if (!me.ip || !me.public) {
        toast.error(t('network_detect_failed', 'Could not read this computer\'s public address. Try again from an office computer.'));
        return;
      }
      if (!addresses.includes(me.ip)) setText((cur) => (cur.trim() ? `${cur.trim()}\n${me.ip}` : me.ip!));
      toast.success(t('network_detected', 'Added {{ip}} — this computer\'s address as KaziPeople sees it. Do this from a computer at the office.', { ip: me.ip }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('network_detect_failed', 'Could not read this computer\'s public address.')));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (blocker) return;
    setBusy('save');
    try {
      await upsertCapturePolicy(branchId, mode, { network_check: check, office_networks: addresses });
      toast.success(t('network_saved', 'Office network saved'));
      onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('network_save_failed', 'Could not save the office network.')));
    } finally {
      setBusy(null);
    }
  };

  const OPTIONS: Array<{ value: NetworkCheck; label: string; hint: string }> = [
    { value: 'off', label: t('network_off', 'Off'), hint: t('network_off_hint', 'Check-in works from anywhere. Where it came from is not checked.') },
    { value: 'flag', label: t('network_flag', 'Flag check-ins from outside the office'), hint: t('network_flag_hint', 'Everyone can check in; ones from another network are marked for the reviewer. The safe way to start.') },
    { value: 'enforce', label: t('network_enforce', 'Only allow from the office'), hint: t('network_enforce_hint', 'Check-in is refused unless the computer is on the office network.') },
  ];

  return (
    <div className="mt-3 w-full space-y-3 rounded-lg bg-muted/40 p-3" data-testid={`office-network-${branchId ?? 'default'}`}>
      <p className="flex items-center gap-2 text-sm font-medium text-charcoal">
        <Wifi className="h-4 w-4" aria-hidden /> {t('network_title', 'Office network')}
      </p>
      <fieldset className="space-y-1.5" disabled={!canWrite}>
        {OPTIONS.map((o) => (
          <label key={o.value} className="flex cursor-pointer items-start gap-2 text-sm">
            <input type="radio" className="mt-1" name={`net-${branchId ?? 'default'}`} checked={check === o.value}
              onChange={() => setCheck(o.value)} />
            <span>
              <span className="text-charcoal">{o.label}</span>
              <span className="block text-xs text-muted-foreground">{o.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {check !== 'off' && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-charcoal" htmlFor={`net-addr-${branchId ?? 'default'}`}>
            {t('network_addresses', "The office's internet address(es)")}
          </label>
          <textarea
            id={`net-addr-${branchId ?? 'default'}`}
            className="min-h-[64px] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
            value={text} disabled={!canWrite} onChange={(e) => setText(e.target.value)}
            placeholder="196.41.33.7"
          />
          <p className="text-xs text-muted-foreground">
            {t('network_addresses_hint', 'One per line. Easiest: open this page on a computer at the office and click the button below.')}
          </p>
          {canWrite && (
            <Button type="button" variant="outline" size="sm" onClick={detect} disabled={busy !== null} className="gap-1.5">
              {busy === 'detect' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
              {t('network_use_mine', "Use this computer's address")}
            </Button>
          )}
        </div>
      )}
      {blocker && <p className="text-xs text-warning" data-testid="network-blocker">{blocker}</p>}
      {canWrite && (
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={busy !== null || !!blocker} className="gap-1.5">
            {busy === 'save' && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('network_save', 'Save office network')}
          </Button>
        </div>
      )}
    </div>
  );
};
